import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaEdit, FaTrash, FaEye, FaToggleOn, FaToggleOff, FaSearch } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
  getLabTests,
  deleteLabTests,
  toggleLabTestStatus,
  clearError,
  clearSuccess,
} from '../../../../redux/Slices/authSlice';
import { useTitle } from '../../../../shared/hooks/TitleContext';


// lab tests component
const LabTests = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const { labTests, labTestsPagination, isLoading, error, success } = useSelector((state) => state.auth);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState('');
  const searchTimeoutRef = useRef(null);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [labTestToDelete, setLabTestToDelete] = useState(null);

  const fetchLabTests = (page = currentPage, search = searchTerm, status = isActiveFilter) => {
    const params = {
      page,
      per_page: 10,
    };
    if (search) params.search = search;
    if (status !== '') params.is_active = status;
    dispatch(getLabTests(params));
  };

  useEffect(() => {
    setTitle('sidenav.laboratoryTests');
  }, [setTitle]);

  useEffect(() => {
    fetchLabTests(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchLabTests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, isActiveFilter]);

  useEffect(() => {
    if (success) {
      const message = typeof success === 'string' ? success.trim() : success;
      if (message) {
        toast.success(message, { rtl: true });
        dispatch(clearSuccess());
        fetchLabTests();
      }
    }
  }, [success, dispatch]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      if (!lastErrorRef.current.message || lastErrorRef.current.message !== error || now - lastErrorRef.current.time > 2000) {
        toast.error(error, { rtl: true });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [error, dispatch]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchLabTests(1, value, isActiveFilter);
    }, 500);
  };

  const handleDeleteConfirmed = async () => {
    if (!labTestToDelete) return;
    await dispatch(deleteLabTests(labTestToDelete.id));
    setDeleteModalOpen(false);
    setLabTestToDelete(null);
    fetchLabTests();
  };

  const handleToggleStatus = async (labTest) => {
    await dispatch(toggleLabTestStatus(labTest.id));
    fetchLabTests();
  };

  const pagination = labTestsPagination || { current_page: currentPage, last_page: 1 };

  const renderStatus = (labTest) => (
    <span
      style={{
        padding: '4px 12px',
        borderRadius: '20px',
        backgroundColor: labTest.is_active ? 'rgba(39,174,96,0.15)' : 'rgba(192,57,43,0.2)',
        color: labTest.is_active ? '#2ecc71' : '#e74c3c',
        fontWeight: 'bold',
        fontSize: '12px',
      }}
    >
      {labTest.is_active ? 'نشط' : 'موقوف'}
    </span>
  );

  return (
    <div style={{ padding: '30px', backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white' }}>
      <div style={{ marginBottom: '24px', display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ margin: 0, fontSize: '24px' }}>الاختبارات </h1>
        <button
          onClick={() => navigate('/lab-tests/add')}
          style={{
            backgroundColor: '#AC2000',
            color: 'white',
            border: 'none',
            padding: '12px 20px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <FaPlus /> إضافة اختبار
        </button>
      </div>

      <div style={{ backgroundColor: '#1b3350', borderRadius: '12px', border: '1px solid #333', padding: '20px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
          <div style={{ flex: 1, minWidth: '220px', position: 'relative' }}>
            <FaSearch style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: '#666' }} />
            <input
              type="text"
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="ابحث عن اختبار..."
              style={{
                width: '100%',
                padding: '12px 16px 12px 40px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: '8px',
                color: 'white',
              }}
            />
          </div>
          <div style={{ minWidth: '200px' }}>
            <select
              value={isActiveFilter}
              onChange={(e) => {
                setIsActiveFilter(e.target.value);
                setCurrentPage(1);
              }}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#1a1f2e',
                border: '1px solid #333',
                borderRadius: '8px',
                color: 'white',
              }}
            >
              <option value="">كل الحالات</option>
              <option value="1">نشط</option>
              <option value="0">موقوف</option>
            </select>
          </div>
        </div>
      </div>

      <div style={{ backgroundColor: '#1b3350', borderRadius: '12px', border: '1px solid #333', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
            <thead>
              <tr style={{ backgroundColor: '#AC2000' }}>
                {['#', 'الاسم بالعربية', 'الاسم بالإنجليزية', 'الحالة', 'الإجراءات'].map((header) => (
                  <th
                    key={header}
                    style={{
                      padding: '15px',
                      textAlign: 'center',
                      color: '#fff',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      borderBottom: '1px solid #333',
                      backgroundColor: '#AC2000'
                    }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#ccc' }}>جاري تحميل البيانات...</td>
                </tr>
              ) : !labTests || labTests.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: 'center', padding: '20px', color: '#ccc' }}>لا توجد بيانات</td>
                </tr>
              ) : (
                labTests.map((labTest, index) => (
                  <tr key={labTest.id} style={{ borderBottom: '1px solid #333' }}>
                    <td style={cellStyle}>{(pagination.current_page - 1) * 10 + (index + 1)}</td>
                    <td style={cellStyle}>{labTest.name_ar || '—'}</td>
                    <td style={cellStyle}>{labTest.name_en || '—'}</td>
                    <td style={cellStyle}>{renderStatus(labTest)}</td>
                    <td style={{ ...cellStyle, minWidth: '200px' }}>
                      <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <ActionButton title="عرض" onClick={() => navigate(`/lab-tests/view/${labTest.id}`)}>
                          <FaEye />
                        </ActionButton>
                        <ActionButton title="تعديل" onClick={() => navigate(`/lab-tests/edit/${labTest.id}`)}>
                          <FaEdit />
                        </ActionButton>
                        <ActionButton
                          title="حذف"
                          bg="#c0392b"
                          onClick={() => {
                            setLabTestToDelete(labTest);
                            setDeleteModalOpen(true);
                          }}
                        >
                          <FaTrash />
                        </ActionButton>
                        <ActionButton title="تغيير الحالة" bg="#34495e" onClick={() => handleToggleStatus(labTest)}>
                          {labTest.is_active ? <FaToggleOn /> : <FaToggleOff />}
                        </ActionButton>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', padding: '16px', borderTop: '1px solid #333' }}>
          <button
            onClick={() => currentPage > 1 && setCurrentPage((prev) => prev - 1)}
            disabled={pagination.current_page === 1}
            style={paginationButton(pagination.current_page === 1)}
          >
            السابق
          </button>
          <span style={{ padding: '8px', color: '#fff' }}>
            {pagination.current_page} / {pagination.last_page || 1}
          </span>
          <button
            onClick={() => pagination.current_page < (pagination.last_page || 1) && setCurrentPage((prev) => prev + 1)}
            disabled={pagination.current_page >= (pagination.last_page || 1)}
            style={paginationButton(pagination.current_page >= (pagination.last_page || 1))}
          >
            التالي
          </button>
        </div>
      </div>
      {deleteModalOpen && (
        <div style={modalOverlayStyle}>
          <div style={modalCardStyle}>
            <h3 style={{ marginTop: 0, marginBottom: 12 }}>تأكيد الحذف</h3>
            <p style={{ marginBottom: 24 }}>
              هل تريد حذف الاختبار{' '}
              <strong>{labTestToDelete?.name_ar || labTestToDelete?.name_en}</strong>؟
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button
                style={modalSecondaryButtonStyle}
                onClick={() => {
                  setDeleteModalOpen(false);
                  setLabTestToDelete(null);
                }}
              >
                إلغاء
              </button>
              <button style={modalPrimaryButtonStyle} onClick={handleDeleteConfirmed}>
                حذف
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const cellStyle = {
  padding: '14px',
  textAlign: 'center',
  color: 'white',
  fontSize: '14px',
};

const ActionButton = ({ children, onClick, title, bg = '#2f3644' }) => (
  <button
    onClick={onClick}
    title={title}
    style={{
      backgroundColor: bg,
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      padding: '8px 10px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '38px',
    }}
  >
    {children}
  </button>
);

const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1000,
  padding: 16,
};

const modalCardStyle = {
  backgroundColor: '#1b3350',
  borderRadius: 12,
  padding: 24,
  border: '1px solid #333',
  width: '100%',
  maxWidth: 420,
};

const modalPrimaryButtonStyle = {
  backgroundColor: '#AC2000',
  color: 'white',
  border: 'none',
  borderRadius: 8,
  padding: '10px 18px',
  cursor: 'pointer',
};

const modalSecondaryButtonStyle = {
  backgroundColor: 'transparent',
  color: 'white',
  border: '1px solid #444',
  borderRadius: 8,
  padding: '10px 18px',
  cursor: 'pointer',
};

const paginationButton = (disabled) => ({
  padding: '8px 16px',
  backgroundColor: disabled ? '#333' : '#1f5ff1',
  color: 'white',
  border: 'none',
  borderRadius: '6px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.6 : 1,
});

export default LabTests;

